import { useEffect, useMemo, useState } from "react";
import { getAllChicagoDCRsvp } from "../../api/rsvpApi";
import { downloadCsv, downloadExcelHtml } from "../../utils/exportUtils";
import AdminModal from "./AdminModal";
import "../../styles/components/RegistrationsAdmin.css";

function toText(value) {
  if (value == null) return "";
  return String(value);
}

function normalizeString(value) {
  return toText(value).trim();
}

function getValue(row, keys) {
  for (const key of keys) {
    if (row && Object.prototype.hasOwnProperty.call(row, key) && row[key] != null) return row[key];
  }
  return "";
}

function getCreatedAt(row) {
  return (
    getValue(row, ["createdAt", "created_at", "created", "timestamp", "createdOn"]) ||
    getValue(row, ["submittedAt", "submitted_at"])
  );
}

function parseDate(value) {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatDateTime(value) {
  const date = parseDate(value);
  if (!date) return "";
  return date.toLocaleString();
}

function humanizeKey(key) {
  return String(key)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^./, (c) => c.toUpperCase());
}

function matchesSearch(row, query) {
  const q = normalizeString(query).toLowerCase();
  if (!q) return true;
  const haystack = [
    getValue(row, ["firstName", "first_name"]),
    getValue(row, ["lastName", "last_name"]),
    getValue(row, ["email"]),
    getValue(row, ["company"]),
  ]
    .map((part) => normalizeString(part).toLowerCase())
    .filter(Boolean)
    .join(" ");
  return haystack.includes(q);
}

function isInDateRange(createdAt, startDate, endDate) {
  if (!startDate && !endDate) return true;
  const created = parseDate(createdAt);
  if (!created) return false;

  if (startDate) {
    const start = new Date(startDate);
    start.setHours(0, 0, 0, 0);
    if (created < start) return false;
  }

  if (endDate) {
    const end = new Date(endDate);
    end.setHours(23, 59, 59, 999);
    if (created > end) return false;
  }

  return true;
}

function extractFirstArray(value, depth = 0) {
  if (Array.isArray(value)) return value;
  if (!value || typeof value !== "object") return null;
  if (depth > 6) return null;

  const commonKeys = ["rsvps", "rsvp", "results", "items", "rows", "data"];
  for (const key of commonKeys) {
    if (Array.isArray(value[key])) return value[key];
  }

  for (const key of commonKeys) {
    const nested = extractFirstArray(value[key], depth + 1);
    if (nested) return nested;
  }

  for (const nestedValue of Object.values(value)) {
    const nested = extractFirstArray(nestedValue, depth + 1);
    if (nested) return nested;
  }

  return null;
}

function normalizeApiList(payload) {
  return extractFirstArray(payload) || [];
}

const exportColumns = [
  { key: "firstName", label: "First Name" },
  { key: "lastName", label: "Last Name" },
  { key: "email", label: "Email" },
  { key: "company", label: "Company" },
  { key: "dinnerSelection", label: "Dinner Selection" },
  { key: "dietaryRestrictions", label: "Dietary Restrictions" },
  { key: "createdAt", label: "Created At" },
];

export default function RegistrationsAdmin() {
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [lastLoadedAt, setLastLoadedAt] = useState(null);

  const [search, setSearch] = useState("");
  const [company, setCompany] = useState("");
  const [dinnerSelection, setDinnerSelection] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const [selected, setSelected] = useState(null);

  async function load() {
    setIsLoading(true);
    setError("");
    try {
      const payload = await getAllChicagoDCRsvp();
      const list = normalizeApiList(payload);
      setItems(list);
      setLastLoadedAt(new Date());
      setPage(1);
    } catch (err) {
      setError(err?.message || "Unable to load registrations.");
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    return items.filter((row) => {
      if (!matchesSearch(row, search)) return false;
      if (company) {
        const value = normalizeString(getValue(row, ["company"]));
        if (value !== company) return false;
      }
      if (dinnerSelection) {
        const value = normalizeString(getValue(row, ["dinnerSelection", "dinner_selection"]));
        if (value !== dinnerSelection) return false;
      }
      if (!isInDateRange(getCreatedAt(row), startDate, endDate)) return false;
      return true;
    });
  }, [company, dinnerSelection, endDate, items, search, startDate]);

  const sorted = useMemo(() => {
    const copy = [...filtered];
    const direction = sortDir === "asc" ? 1 : -1;

    function compareText(a, b) {
      return normalizeString(a).localeCompare(normalizeString(b), undefined, { sensitivity: "base" });
    }

    copy.sort((a, b) => {
      if (sortKey === "name") {
        const aName = `${getValue(a, ["lastName", "last_name"])} ${getValue(a, ["firstName", "first_name"])}`;
        const bName = `${getValue(b, ["lastName", "last_name"])} ${getValue(b, ["firstName", "first_name"])}`;
        return compareText(aName, bName) * direction;
      }
      if (sortKey === "email") return compareText(getValue(a, ["email"]), getValue(b, ["email"])) * direction;
      if (sortKey === "company") return compareText(getValue(a, ["company"]), getValue(b, ["company"])) * direction;

      const aCreated = parseDate(getCreatedAt(a))?.getTime() ?? 0;
      const bCreated = parseDate(getCreatedAt(b))?.getTime() ?? 0;
      return (aCreated - bCreated) * direction;
    });

    return copy;
  }, [filtered, sortDir, sortKey]);

  const pageCount = Math.max(1, Math.ceil(sorted.length / pageSize));
  const currentPage = Math.min(page, pageCount);
  const pageRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [currentPage, pageSize, sorted]);

  const companies = useMemo(() => {
    const values = new Set();
    items.forEach((row) => {
      const value = normalizeString(getValue(row, ["company"]));
      if (value) values.add(value);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [items]);

  const dinnerOptions = useMemo(() => {
    const values = new Set();
    items.forEach((row) => {
      const value = normalizeString(getValue(row, ["dinnerSelection", "dinner_selection"]));
      if (value) values.add(value);
    });
    return Array.from(values).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: "base" }));
  }, [items]);

  function makeExportRows(rows) {
    return rows.map((row) => ({
      firstName: normalizeString(getValue(row, ["firstName", "first_name"])),
      lastName: normalizeString(getValue(row, ["lastName", "last_name"])),
      email: normalizeString(getValue(row, ["email"])),
      company: normalizeString(getValue(row, ["company"])),
      dinnerSelection: normalizeString(getValue(row, ["dinnerSelection", "dinner_selection"])),
      dietaryRestrictions: normalizeString(getValue(row, ["dietaryRestrictions", "dietary_restrictions"])),
      createdAt: formatDateTime(getCreatedAt(row)),
    }));
  }

  function handleExport(kind, scope) {
    const base = `mhdc_chicago_registrations_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}`;
    const rows = scope === "all" ? items : filtered;
    const exportRows = makeExportRows(rows);
    if (kind === "csv") {
      downloadCsv(`${base}_${scope}.csv`, exportRows, exportColumns);
    } else {
      downloadExcelHtml(`${base}_${scope}.xls`, exportRows, exportColumns);
    }
  }

  return (
    <section className="adminRegs" aria-label="Registrations">
      <div className="adminRegs__topbar">
        <div>
          <h2 className="adminRegs__title">Registrations</h2>
          <div className="adminRegs__meta">
            <span>
              Showing {filtered.length} of {items.length}
            </span>
            {lastLoadedAt ? <span>Last loaded: {lastLoadedAt.toLocaleString()}</span> : null}
          </div>
        </div>
        <div className="adminRegs__actions">
          <button className="adminRegs__button" type="button" onClick={load} disabled={isLoading}>
            {isLoading ? "Loading..." : items.length ? "Refresh" : "Load"}
          </button>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => handleExport("csv", "filtered")}
            disabled={!filtered.length}
          >
            Export CSV (filtered)
          </button>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => handleExport("excel", "filtered")}
            disabled={!filtered.length}
          >
            Export Excel (filtered)
          </button>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => handleExport("csv", "all")}
            disabled={!items.length}
          >
            Export CSV (all)
          </button>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => handleExport("excel", "all")}
            disabled={!items.length}
          >
            Export Excel (all)
          </button>
        </div>
      </div>

      {error ? (
        <div className="adminRegs__error" role="alert">
          {error}
        </div>
      ) : null}

      <div className="adminRegs__filters" aria-label="Search and filters">
        <div className="adminRegs__filter">
          <label className="adminRegs__label" htmlFor="admin-search">
            Search
          </label>
          <input
            className="adminRegs__input"
            id="admin-search"
            type="search"
            placeholder="Name, email, company…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="adminRegs__filter">
          <label className="adminRegs__label" htmlFor="admin-company">
            Company
          </label>
          <select
            className="adminRegs__select"
            id="admin-company"
            value={company}
            onChange={(e) => {
              setCompany(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            {companies.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="adminRegs__filter">
          <label className="adminRegs__label" htmlFor="admin-dinner">
            Dinner
          </label>
          <select
            className="adminRegs__select"
            id="admin-dinner"
            value={dinnerSelection}
            onChange={(e) => {
              setDinnerSelection(e.target.value);
              setPage(1);
            }}
          >
            <option value="">All</option>
            {dinnerOptions.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        </div>

        <div className="adminRegs__filter">
          <label className="adminRegs__label" htmlFor="admin-start">
            Start date
          </label>
          <input
            className="adminRegs__input"
            id="admin-start"
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="adminRegs__filter">
          <label className="adminRegs__label" htmlFor="admin-end">
            End date
          </label>
          <input
            className="adminRegs__input"
            id="admin-end"
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
          />
        </div>

        <div className="adminRegs__filter adminRegs__filter--actions">
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => {
              setSearch("");
              setCompany("");
              setDinnerSelection("");
              setStartDate("");
              setEndDate("");
              setPage(1);
            }}
            disabled={!search && !company && !dinnerSelection && !startDate && !endDate}
          >
            Clear filters
          </button>
        </div>
      </div>

      <div className="adminRegs__tableWrap" role="region" aria-label="Registration table">
        <table className="adminRegs__table">
          <thead>
            <tr>
              <th>
                <button
                  className="adminRegs__sort"
                  type="button"
                  onClick={() => {
                    setSortKey("name");
                    setSortDir(sortKey === "name" && sortDir === "asc" ? "desc" : "asc");
                  }}
                >
                  Name
                </button>
              </th>
              <th>
                <button
                  className="adminRegs__sort"
                  type="button"
                  onClick={() => {
                    setSortKey("email");
                    setSortDir(sortKey === "email" && sortDir === "asc" ? "desc" : "asc");
                  }}
                >
                  Email
                </button>
              </th>
              <th>
                <button
                  className="adminRegs__sort"
                  type="button"
                  onClick={() => {
                    setSortKey("company");
                    setSortDir(sortKey === "company" && sortDir === "asc" ? "desc" : "asc");
                  }}
                >
                  Company
                </button>
              </th>
              <th>Dinner Selection</th>
              <th>Dietary Restrictions</th>
              <th>
                <button
                  className="adminRegs__sort"
                  type="button"
                  onClick={() => {
                    setSortKey("createdAt");
                    setSortDir(sortKey === "createdAt" && sortDir === "asc" ? "desc" : "asc");
                  }}
                >
                  Created
                </button>
              </th>
              <th>View</th>
            </tr>
          </thead>
          <tbody>
            {!items.length ? (
              <tr>
                <td className="adminRegs__empty" colSpan={7}>
                  {lastLoadedAt ? "No registrations returned." : "Loading registrations..."}
                </td>
              </tr>
            ) : !filtered.length ? (
              <tr>
                <td className="adminRegs__empty" colSpan={7}>
                  No matches for current filters.
                </td>
              </tr>
            ) : (
              pageRows.map((row, idx) => {
                const firstName = normalizeString(getValue(row, ["firstName", "first_name"]));
                const lastName = normalizeString(getValue(row, ["lastName", "last_name"]));
                const name = `${firstName} ${lastName}`.trim();
                const email = normalizeString(getValue(row, ["email"]));
                const key = normalizeString(getValue(row, ["id", "_id"])) || `${email}-${getCreatedAt(row)}-${idx}`;
                return (
                  <tr key={key}>
                    <td>{name || "—"}</td>
                    <td className="adminRegs__mono">{email || "—"}</td>
                    <td>{normalizeString(getValue(row, ["company"])) || "—"}</td>
                    <td>{normalizeString(getValue(row, ["dinnerSelection", "dinner_selection"])) || "—"}</td>
                    <td className="adminRegs__truncate">
                      {normalizeString(getValue(row, ["dietaryRestrictions", "dietary_restrictions"])) || "—"}
                    </td>
                    <td className="adminRegs__mono">{formatDateTime(getCreatedAt(row)) || "—"}</td>
                    <td>
                      <button className="adminRegs__link" type="button" onClick={() => setSelected(row)}>
                        Details
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <div className="adminRegs__footer">
        <div className="adminRegs__pager">
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => setPage(1)}
            disabled={currentPage <= 1}
          >
            First
          </button>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={currentPage <= 1}
          >
            Prev
          </button>
          <div className="adminRegs__pageInfo">
            Page {currentPage} of {pageCount}
          </div>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
            disabled={currentPage >= pageCount}
          >
            Next
          </button>
          <button
            className="adminRegs__button adminRegs__button--secondary"
            type="button"
            onClick={() => setPage(pageCount)}
            disabled={currentPage >= pageCount}
          >
            Last
          </button>
        </div>

        <div className="adminRegs__pageSize">
          <label className="adminRegs__label" htmlFor="admin-pageSize">
            Rows
          </label>
          <select
            className="adminRegs__select"
            id="admin-pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
          >
            {[10, 25, 50, 100].map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>
      </div>

      <AdminModal
        open={Boolean(selected)}
        title="Registration Details"
        onClose={() => setSelected(null)}
      >
        {selected ? (
          <div className="adminRegs__detail">
            <div className="adminRegs__detailGrid">
              {(() => {
                const ordered = [
                  { key: "firstName", label: "First Name", value: normalizeString(getValue(selected, ["firstName", "first_name"])) },
                  { key: "lastName", label: "Last Name", value: normalizeString(getValue(selected, ["lastName", "last_name"])) },
                  { key: "email", label: "Email", value: normalizeString(getValue(selected, ["email"])) },
                  { key: "company", label: "Company", value: normalizeString(getValue(selected, ["company"])) },
                  {
                    key: "dinnerSelection",
                    label: "Dinner Selection",
                    value: normalizeString(getValue(selected, ["dinnerSelection", "dinner_selection"])),
                  },
                  {
                    key: "dietaryRestrictions",
                    label: "Dietary Restrictions",
                    value: normalizeString(getValue(selected, ["dietaryRestrictions", "dietary_restrictions"])),
                  },
                  {
                    key: "createdAt",
                    label: "Created At",
                    value: formatDateTime(getCreatedAt(selected)) || toText(getCreatedAt(selected)),
                  },
                  {
                    key: "updatedAt",
                    label: "Updated At",
                    value:
                      formatDateTime(getValue(selected, ["updatedAt", "updated_at"])) ||
                      toText(getValue(selected, ["updatedAt", "updated_at"])),
                  },
                  { key: "id", label: "ID", value: normalizeString(getValue(selected, ["id", "_id"])) },
                ];

                const usedKeys = new Set([
                  "firstName",
                  "first_name",
                  "lastName",
                  "last_name",
                  "email",
                  "company",
                  "dinnerSelection",
                  "dinner_selection",
                  "dietaryRestrictions",
                  "dietary_restrictions",
                  "createdAt",
                  "created_at",
                  "created",
                  "timestamp",
                  "createdOn",
                  "submittedAt",
                  "submitted_at",
                  "updatedAt",
                  "updated_at",
                  "id",
                  "_id",
                ]);

                const extras = Object.entries(selected)
                  .filter(([key]) => !usedKeys.has(key))
                  .sort(([a], [b]) => a.localeCompare(b, undefined, { sensitivity: "base" }))
                  .map(([key, value]) => ({ key, label: humanizeKey(key), value: toText(value) }));

                return [...ordered, ...extras].map((item) => (
                  <div className="adminRegs__detailRow" key={item.key}>
                    <div className="adminRegs__detailKey">{item.label}</div>
                    <div className="adminRegs__detailValue">{item.value || "—"}</div>
                  </div>
                ));
              })()}
            </div>
          </div>
        ) : null}
      </AdminModal>
    </section>
  );
}
